/**
 * Native federated sign-in (Phase 9).
 *
 * Native only. Uses @capgo/capacitor-social-login to run Google sign-in through
 * the platform account picker — no webview navigation, so the Capacitor bridge
 * (and Health Connect) survive. Returns a Google ID token that the backend
 * verifies (POST /api/auth/google/native) and exchanges for our bearer JWT.
 *
 * On web this is unused; the web app keeps its redirect-based Google flow.
 */
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';

// Web OAuth client id — used as the Android serverClientId so the ID token's
// audience is our backend. Public identifier, safe to ship in the bundle.
const WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

let initialized = false;

export const nativeGoogleSupported = () => {
  try {
    return Capacitor?.isNativePlatform?.() === true && !!WEB_CLIENT_ID;
  } catch {
    return false;
  }
};

async function ensureInitialized() {
  if (initialized) return;
  await SocialLogin.initialize({ google: { webClientId: WEB_CLIENT_ID } });
  initialized = true;
}

/**
 * Run the native Google account picker and return the Google ID token (a JWT).
 * Throws if sign-in is cancelled or yields no token.
 */
export async function nativeGoogleIdToken() {
  if (!WEB_CLIENT_ID) {
    throw new Error('Google sign-in is not configured (missing web client id).');
  }
  await ensureInitialized();
  // No `scopes`: we only need the ID token to authenticate (it already carries
  // email + basic profile). Requesting extra scopes triggers Google's
  // authorization-code flow, which the plugin requires a MainActivity change for
  // ("you cannot use scopes without modifying the main activity") — unnecessary
  // for sign-in.
  const res = await SocialLogin.login({
    provider: 'google',
    options: {},
  });
  const idToken = res?.result?.idToken;
  if (!idToken) {
    throw new Error('Google sign-in did not return an ID token.');
  }
  return idToken;
}
